/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Title from "../../components/Form/Title";
import Moneda from "../../components/Presupuesto/Moneda";
import useLogin from "../../hooks/useLogin";
import { PresupuestoResumen } from "../../service/presupuestoExcel";
import "./dashboard.css";

const IVA = 0.16;

function Tarjeta({ icon, accent, label, value, pie }) {
  return (
    <div className="crud-card stat-tile">
      <div className={`stat-tile-icon accent-${accent}`}>
        <i className={`bi ${icon}`}></i>
      </div>
      <div>
        <div className="stat-tile-label">{label}</div>
        <div className="stat-tile-value">{value}</div>
        {pie && <div className="stat-tile-pie">{pie}</div>}
      </div>
    </div>
  );
}

/** Barra con la parte del presupuesto que le toca a cada renglón. */
function Barra({ titulo, monto, total, tono }) {
  const parte = total > 0 ? (monto / total) * 100 : 0;

  return (
    <div className="tablero-barra">
      <div className="tablero-barra-fila">
        <span className="tablero-barra-titulo">{titulo}</span>
        <span className="tablero-barra-monto">
          <Moneda valor={monto} /> · {parte.toFixed(1)}%
        </span>
      </div>
      <div className="tablero-barra-riel">
        <div
          className={`tablero-barra-relleno tono-${tono}`}
          style={{ width: `${parte}%` }}
        />
      </div>
    </div>
  );
}

/**
 * Tablero del proyecto.
 *
 * Sale del mismo resumen que arma el Excel, así que lo que se ve aquí cuadra
 * con el desglose y con el archivo que se descarga. Antes eran seis cifras
 * sueltas, y tres de ellas se sacaban a ojo multiplicando lo pagado por 1.16.
 */
export default function Index() {
  const { token } = useLogin();
  const { id } = useParams();

  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelado = false;

    setCargando(true);
    PresupuestoResumen(id, token)
      .then((respuesta) => {
        if (cancelado) return;
        if (!respuesta?.success) {
          throw new Error(
            respuesta?.message || "No se pudo cargar el presupuesto"
          );
        }
        setDatos(respuesta.data);
      })
      .catch((e) => {
        if (!cancelado) setError(e.message);
      })
      .finally(() => {
        if (!cancelado) setCargando(false);
      });

    return () => {
      cancelado = true;
    };
  }, [id, token]);

  const resumen = useMemo(() => {
    if (!datos) return null;

    const total = datos.portada.gran_total;
    const subtotal = total / (1 + IVA);

    // Lo ejercido se lleva en los informes semestrales.
    const ejercido = datos.esquema.reduce(
      (suma, informe) => suma + (informe.totales.ejercido || 0),
      0
    );

    return {
      total,
      subtotal,
      iva: total - subtotal,
      ejercido,
      porEjercer: total - ejercido,
      etapas: datos.portada.etapas.map((etapa) => ({
        titulo: etapa.titulo,
        monto: etapa.subtotal,
      })),
      cuentas: datos.portada.etapas
        .flatMap((etapa) => etapa.renglones)
        .sort((a, b) => b.total - a.total)
        .slice(0, 8),
      aportantes: datos.aportantes.map((a) => ({
        ...a,
        monto: datos.portada.por_aportante[a.id] || 0,
      })),
    };
  }, [datos]);

  const porcentaje = (monto) =>
    resumen && resumen.total > 0
      ? `${((monto / resumen.total) * 100).toFixed(1)}%`
      : "—";

  return (
    <section className="tablero">
      <div className="container">
        <nav aria-label="breadcrumb" className="crud-breadcrumb">
          <ol className="breadcrumb mb-0">
            <li className="breadcrumb-item">
              <Link to={"/proyectos"}>Proyectos</Link>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              Tablero
            </li>
          </ol>
        </nav>

        <div className="crud-header">
          <Title
            title={datos?.proyecto?.nombre || "Tablero"}
            className="crud-title"
          />
          {datos && (
            <Link className="crud-secondary-btn" to={`/presupuestos/${id}`}>
              <i className="bi bi-list-columns"></i>
              Ver el desglose
            </Link>
          )}
        </div>

        {cargando && (
          <div className="crud-card tablero-vacio">Cargando el proyecto...</div>
        )}

        {!cargando && error && (
          <div className="crud-card tablero-vacio">{error}</div>
        )}

        {!cargando && !error && resumen && (
          <>
            <div className="stat-section-title">Presupuesto</div>
            <div className="stat-grid">
              <Tarjeta
                icon="bi-cash-stack"
                accent="orange"
                label="Subtotal"
                value={<Moneda valor={resumen.subtotal} />}
              />
              <Tarjeta
                icon="bi-percent"
                accent="orange"
                label="IVA"
                value={<Moneda valor={resumen.iva} />}
              />
              <Tarjeta
                icon="bi-cash-coin"
                accent="orange"
                label="Total del proyecto"
                value={<Moneda valor={resumen.total} />}
              />
            </div>

            <div className="stat-section-title">Ejercido</div>
            <div className="stat-grid">
              <Tarjeta
                icon="bi-receipt"
                accent="blue"
                label="Ejercido"
                value={<Moneda valor={resumen.ejercido} />}
                pie={`${porcentaje(resumen.ejercido)} del presupuesto`}
              />
              <Tarjeta
                icon="bi-hourglass-split"
                accent="blue"
                label="Por ejercer"
                value={<Moneda valor={resumen.porEjercer} />}
                pie={`${porcentaje(resumen.porEjercer)} del presupuesto`}
              />
              <Tarjeta
                icon="bi-people"
                accent="blue"
                label="Aportantes"
                value={resumen.aportantes.length}
                pie={resumen.aportantes.map((a) => a.nombre).join(", ")}
              />
            </div>

            <div className="tablero-columnas">
              <div className="crud-card">
                <h3 className="tablero-titulo">En qué se va el presupuesto</h3>
                {resumen.etapas.map((etapa, i) => (
                  <Barra
                    key={etapa.titulo}
                    titulo={etapa.titulo}
                    monto={etapa.monto}
                    total={resumen.total}
                    tono={i % 4}
                  />
                ))}
              </div>

              <div className="crud-card">
                <h3 className="tablero-titulo">Quién aporta</h3>
                {resumen.aportantes.length === 0 ? (
                  <p className="tablero-vacio">
                    Este proyecto todavía no tiene aportantes.
                  </p>
                ) : (
                  resumen.aportantes.map((a, i) => (
                    <Barra
                      key={a.id}
                      titulo={a.nombre}
                      monto={a.monto}
                      total={resumen.total}
                      tono={i % 4}
                    />
                  ))
                )}
              </div>
            </div>

            <div className="crud-card">
              <h3 className="tablero-titulo">Las cuentas más caras</h3>
              <div className="dtable-scroll">
                <table className="dtable">
                  <thead>
                    <tr>
                      <th>Cuenta</th>
                      <th>Nombre</th>
                      <th className="text-end">Total</th>
                      <th className="text-end">Del presupuesto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resumen.cuentas.map((cuenta) => (
                      <tr key={cuenta.cuenta}>
                        <td>{cuenta.cuenta}</td>
                        <td>{cuenta.nombre}</td>
                        <td className="text-end">
                          <Moneda valor={cuenta.total} />
                        </td>
                        <td className="text-end">{porcentaje(cuenta.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
